import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Check, Loader2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  id: string;
  label: string;
  hint?: string | undefined;
  meta?: string | undefined;
  /** Ersetzt die Standarddarstellung der Zeile, z. B. um Bestand und Preis zu zeigen. */
  render?: ReactNode;
}

export interface SearchComboboxProps {
  id?: string | undefined;
  value: string;
  placeholder: string;
  options: readonly ComboboxOption[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: (() => void) | undefined;
  multiple?: boolean;
  selectedIds?: readonly string[];
  disabled?: boolean;
  emptyLabel: string;
  ariaLabel: string;
  ariaDescribedBy?: string | undefined;
  ariaInvalid?: boolean | undefined;
  autoFocus?: boolean;
  onQueryChange: (query: string) => void;
  onSelect: (option: ComboboxOption) => void;
  className?: string;
}

/**
 * Auto-Suggest-Feld mit vollständiger Tastaturbedienung (Pfeile, Enter, Escape).
 * Die Liste hängt über ein Radix-Portal am `body`, damit scrollende Tabellencontainer
 * sie nicht abschneiden.
 */
export function SearchCombobox({
  id,
  value,
  placeholder,
  options,
  isLoading = false,
  isError = false,
  onRetry,
  multiple = false,
  selectedIds = [],
  disabled = false,
  emptyLabel,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  autoFocus = false,
  onQueryChange,
  onSelect,
  className,
}: SearchComboboxProps): JSX.Element {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const activeRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const active = activeRef.current;
    const content = contentRef.current;
    if (!active || !content) return;
    // Scroll only the portalled result list; scrolling page ancestors moves its anchor away.
    const option = active.getBoundingClientRect();
    const viewport = content.getBoundingClientRect();
    if (option.top < viewport.top) content.scrollTop += option.top - viewport.top;
    else if (option.bottom > viewport.bottom) content.scrollTop += option.bottom - viewport.bottom;
  }, [activeIndex, options]);

  function choose(option: ComboboxOption): void {
    onSelect(option);
    if (!multiple) {
      setQuery(option.label);
      setOpen(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    // Let the enclosing form handle save shortcuts without selecting another value.
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(0, Math.min(open ? index + 1 : 0, options.length - 1)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Tab') setOpen(false);
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = options[activeIndex];
      if (option !== undefined) {
        event.preventDefault();
        choose(option);
      }
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className={cn('relative', className)}>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id={id}
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                open && options[activeIndex] ? `${listId}-${activeIndex}` : undefined
              }
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
              autoComplete="off"
              autoFocus={autoFocus}
              disabled={disabled}
              className="pl-8"
              placeholder={placeholder}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                onQueryChange(event.target.value);
                setActiveIndex(0);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onKeyDown={handleKeyDown}
            />
            {isLoading ? (
              <Loader2
                aria-hidden="true"
                className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
              />
            ) : null}
          </div>
        </div>
      </PopoverAnchor>

      <PopoverContent
        ref={contentRef}
        side="bottom"
        align="start"
        sideOffset={4}
        collisionPadding={8}
        className="max-h-[min(320px,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] min-w-[min(320px,calc(100vw-24px))] max-w-[calc(100vw-24px)] overflow-y-auto overscroll-contain p-1"
        onInteractOutside={(event) => {
          // The input belongs to the selection area although Radix portals the list.
          if (event.target instanceof Node && anchorRef.current?.contains(event.target))
            event.preventDefault();
        }}
        onEscapeKeyDown={(event) => event.stopPropagation()}
        // Der Eingabefokus muss beim Tippen erhalten bleiben, sonst bricht die Suche ab.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          aria-multiselectable={multiple || undefined}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <li role="status" className="px-2 py-3 text-sm text-muted-foreground">
              {t('common.loading')}
            </li>
          ) : null}
          {isError ? (
            <li role="status" className="px-2 py-3 text-sm text-destructive">
              {t('international.searchError')}
              {onRetry ? (
                <button type="button" className="ml-2 underline" onClick={onRetry}>
                  {t('common.retry')}
                </button>
              ) : null}
            </li>
          ) : null}
          {options.length === 0 && !isLoading && !isError ? (
            <li className="px-2 py-3 text-sm text-muted-foreground">{emptyLabel}</li>
          ) : (
            options.map((option, index) => (
              <li key={option.id}>
                <button
                  ref={index === activeIndex ? activeRef : null}
                  id={`${listId}-${index}`}
                  type="button"
                  tabIndex={-1}
                  title={option.label}
                  role="option"
                  aria-selected={
                    multiple ? selectedIds.includes(option.id) : option.label === value
                  }
                  className={cn(
                    'flex min-h-11 w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    index === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent',
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(option)}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0',
                      multiple && 'rounded border border-muted-foreground',
                    )}
                  >
                    <Check
                      aria-hidden="true"
                      className={cn(
                        'h-full w-full',
                        (multiple ? selectedIds.includes(option.id) : option.label === value)
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  </span>
                  {option.render ?? (
                    <>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{option.label}</span>
                        {option.hint === undefined ? null : (
                          <span className="block truncate text-xs text-muted-foreground">
                            {option.hint}
                          </span>
                        )}
                      </span>
                      {option.meta === undefined ? null : (
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          {option.meta}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
